import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Checkbox, Input, Select } from '../atoms';
import { StatCard, StatCardRow, useToast } from '../organisms';
import { useEntityPermission } from '../lib/usePermissions';
import { useIsCompact } from '../lib/useMediaQuery';
import { useCurrentUser, currentUserName } from '../lib/useCurrentUser';
import { STATE_FILTER_OPTIONS } from '../lib/us-states';
import { useOrgTimezone, formatEventDate, formatEventTime, formatAbsInTz } from '../lib/orgTimezone';
import {
  CalendarEvent, EventType, UserLite, NamedRow, DateConfidence,
  TYPE_META, TYPE_ORDER, metaFor, EventModal, EventLinkage, userLabel, eventTitle, sourceLabel,
  isTentative, TentativeBadge, tentativeRowStyle,
} from './CalendarShared';
import { CalendarSubscribe } from './CalendarSubscribe';

type CalendarView = 'grid' | 'agenda';

/**
 * CalendarPage — /calendar
 *
 * The org's first-class Calendar (lane "calendar", V1) over the calendar_events
 * entity: the dated things in the supplier journey — coaching sessions and
 * workshops, cohort start/end milestones, assessment due dates, referral
 * follow-ups, information-request deadlines, and plain reminders.
 *
 * Two views via a toggle: a true MONTH GRID (weeks × days, events placed on their
 * date) and an AGENDA list (the whole window date-grouped). The grid is dense for
 * a phone, so below the shell breakpoint the agenda is forced. Per-ASSIGNEE
 * (assigned_user_id) and per-BUSINESS (client_id) filters narrow the window — both
 * are real backend query params that AND with server-side book scope. Every event
 * CLICKS THROUGH to its source record: the linkage chip is a Link into
 * /clients/:id, and the edit modal carries the same "Linked to" shortcut.
 *
 * Reads GET /api/calendar/events?from&to[&type&assigned_user_id&client_id] for the CURRENT window+scope —
 * the backend row-scopes to the caller's book (agent/analyst see only events on
 * businesses on their book, or assigned to / created by them; admin/super_admin
 * firm-wide). The page never fetches "everything": it always queries by the
 * visible month's [from,to] window.
 *
 * Create/edit uses a modal with the six event types, date/time/location, an
 * assign-to (staff user), and the one business link. Filters narrow by type.
 *
 * Style matches the existing operator pages (design tokens, atoms/organisms) — no
 * new component library. Forward-compatible with the M3 tonal-green direction via
 * the shared card/surface tokens.
 */

// Event shape, type metadata, and the EventModal now live in ./CalendarShared so
// the org calendar and the per-client Calendar tab render the same surface.

// ── date helpers (all local, no library) ──────────────────────────────────────
function iso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
function firstOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function lastOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d: Date, n: number): Date { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * The DEADLINE-shaped event types — the ones where the date is an obligation whose
 * passing has a consequence, as opposed to something merely scheduled (a session)
 * or noted (a reminder, a cohort milestone). These are what the "Deadlines only"
 * toggle and the "Past due" tile count.
 *
 * Mirrors DEADLINE_KINDS in src/services/calendar.js, which applies proximity
 * urgency to the same idea on the read-only projection.
 */
const DEADLINE_TYPES: readonly EventType[] = ['info_request_due', 'assessment_due'];

/** The small uppercase caption over the From/To date inputs in the filter row. */
const filterLabelStyle: CSSProperties = {
  fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
  letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
};

// The visible grid runs Sun-of-first-week … Sat-of-last-week so the month is a
// clean 6×7 (or 5×7) rectangle. We fetch that whole visible window.
function gridBounds(month: Date): { start: Date; end: Date } {
  const start = firstOfMonth(month);
  start.setDate(start.getDate() - start.getDay());
  const end = lastOfMonth(month);
  end.setDate(end.getDate() + (6 - end.getDay()));
  return { start, end };
}

export function CalendarPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const canView = useEntityPermission('event', 'can_view');
  const canAdd = useEntityPermission('event', 'can_add');
  const canEdit = useEntityPermission('event', 'can_edit');
  const canDelete = useEntityPermission('event', 'can_delete');

  const isCompact = useIsCompact();
  // The org's IANA timezone (organizations.timezone via /api/current-user): every
  // event_time / remind_at renders in THIS zone, and event_date is shown as a
  // clean local date — never a raw ISO (calendar-tz-grid).
  const orgTz = useOrgTimezone();
  const [month, setMonth] = useState<Date>(() => {
    // Deep-link ?date=YYYY-MM-DD (from a dashboard "Upcoming events" click) opens
    // the calendar on that event's month, so the event is in the loaded window.
    const d = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('date') : null;
    if (d) { const [y, m] = d.split('-').map(Number); if (y && m) return new Date(y, m - 1, 1); }
    return firstOfMonth(new Date());
  });
  // Seed the type filter from the URL (?type=session) so a dashboard "Upcoming
  // sessions" click lands on the calendar already filtered to those sessions.
  const [searchParams] = useSearchParams();
  const [typeFilter, setTypeFilter] = useState<'' | EventType>(() => {
    const t = searchParams.get('type');
    return t && (TYPE_ORDER as readonly string[]).includes(t) ? (t as EventType) : '';
  });
  // per-ASSIGNEE (assigned_user_id) and per-BUSINESS (client_id) scope filters —
  // wired to the real backend query params (both AND with book scope server-side).
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  // REGION — IGNITE! Central works nationally, so "where is this?" is the event's
  // own `state` column, resolved server-side. An event with no state is correctly
  // excluded when a region is chosen.
  const [regionFilter, setRegionFilter] = useState('');
  // A session or workshop date is often tentative when it is first penciled in and
  // confirmed later. This is the "which dates still need confirming?" cut — a real
  // backend param that ANDs with book scope, so a manager can work that list.
  const [confidenceFilter, setConfidenceFilter] = useState<'' | DateConfidence>('');
  // Date-range filters (YYYY-MM-DD). When either bound is set the calendar leaves
  // month-grid mode: it fetches from=rangeFrom / to=rangeTo (an arbitrary window
  // that can span months) instead of the visible month, and forces the agenda.
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  // "Deadlines only" narrows the already-fetched window (client-side) to the two
  // deadline-shaped types, composing with the date range to yield a deadline range.
  const [deadlinesOnly, setDeadlinesOnly] = useState(false);
  const [view, setView] = useState<CalendarView>('grid');
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [creating, setCreating] = useState(false);

  // Deep-link: /calendar?event=<id>&date=<YYYY-MM-DD> (from a dashboard "Upcoming
  // events" click). The month was already seeded from ?date above so this event's
  // window is loaded; the open effect below finds it in the grid's own events and
  // pops its modal. (See after rawEvents.)
  const focusEventId = searchParams.get('event');

  // The signed-in user pins a "Me" option atop the assignee filter.
  const me = useCurrentUser();
  const meId = me?.id ?? '';
  const meName = currentUserName(me);

  const rangeActive = !!(rangeFrom || rangeTo);
  // A backward range (from after to) would 400 at the backend — skip the query
  // and surface an inline hint instead.
  const rangeInvalid = !!rangeFrom && !!rangeTo && rangeFrom > rangeTo;

  // The month grid is dense for a phone; below the shell breakpoint we always
  // render the agenda. Range mode also forces the agenda — a month grid can't
  // represent an arbitrary multi-month window.
  const effectiveView: CalendarView = (isCompact || rangeActive) ? 'agenda' : view;

  // The grid cells always follow the selected month (rendered only out of range
  // mode); the fetched WINDOW is the explicit range when set, else the month grid.
  const { start, end } = gridBounds(month);
  const gridFrom = iso(start);
  const gridTo = iso(end);
  const queryFrom = rangeActive ? (rangeFrom || undefined) : gridFrom;
  const queryTo = rangeActive ? (rangeTo || undefined) : gridTo;

  const eventsQuery = useQuery<CalendarEvent[]>({
    queryKey: ['calendar-events', rangeActive ? 'range' : 'month', queryFrom ?? '', queryTo ?? '', typeFilter, assigneeFilter, clientFilter, regionFilter, confidenceFilter],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (queryFrom) params.set('from', queryFrom);
      if (queryTo) params.set('to', queryTo);
      if (typeFilter) params.set('type', typeFilter);
      if (assigneeFilter) params.set('assigned_user_id', assigneeFilter);
      if (clientFilter) params.set('client_id', clientFilter);
      if (regionFilter) params.set('state', regionFilter);
      if (confidenceFilter) params.set('date_confidence', confidenceFilter);
      return api.get<CalendarEvent[]>(`/api/calendar/events?${params.toString()}`, signal);
    },
    enabled: canView && !rangeInvalid,
  });

  // FK option lists for the modal — fetched lazily (only after view is granted).
  const usersQuery = useQuery<UserLite[]>({
    queryKey: ['calendar-users-list'],
    queryFn: ({ signal }) => api.get<UserLite[]>('/api/users-list', signal),
    enabled: canView,
  });
  // Business options for the modal's one linkage picker and for the Business filter.
  const clientsQuery = useQuery<NamedRow[]>({
    queryKey: ['calendar-clients-list'],
    queryFn: ({ signal }) => api.get<NamedRow[]>('/api/clients', signal),
    enabled: canView,
  });

  // Event mutations change the calendar_events table, which also backs the
  // Dashboard "Upcoming Events" box (['calendar-upcoming']) and each client's
  // Calendar tab (['client-calendar-events']) — refresh all three, or those
  // derived surfaces stay stale under the 60s staleTime.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-upcoming'] });
    queryClient.invalidateQueries({ queryKey: ['client-calendar-events'] });
  };

  const createMut = useMutation({
    mutationFn: (body: Partial<CalendarEvent>) => api.post<CalendarEvent>('/api/calendar/events', body),
    onSuccess: () => { invalidate(); setCreating(false); toast({ variant: 'success', message: 'Event created.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not create the event.' }),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CalendarEvent> }) => api.put<CalendarEvent>(`/api/calendar/events/${id}`, body),
    onSuccess: () => { invalidate(); setEditing(null); toast({ variant: 'success', message: 'Event updated.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not update the event.' }),
  });
  // B4 — tentative ⇄ confirmed. Its own endpoint (a compare-and-swap transition,
  // audit-logged), NOT part of the generic save, so a routine edit can never flip a
  // date's standing as a side effect.
  const confidenceMut = useMutation({
    mutationFn: ({ id, to }: { id: string; to: DateConfidence }) =>
      api.post<CalendarEvent>(`/api/calendar/events/${id}/date-confidence`, { to }),
    onSuccess: (row) => {
      invalidate();
      setEditing(row);
      toast({ variant: 'success', message: row.date_confidence === 'confirmed' ? 'Date confirmed.' : 'Date marked tentative.' });
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not update the date.' }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/api/calendar/events/${id}`),
    onSuccess: () => { invalidate(); setEditing(null); toast({ variant: 'success', message: 'Event deleted.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not delete the event.' }),
  });
  // Rebuild the PROGRAM-DELIVERY projection onto the calendar: coaching sessions
  // and workshops (program_sessions), cohort start/end milestones, and
  // information-request deadlines. Every generated event has a deterministic id and
  // is upserted, so this is idempotent — the explicit "pull it all now".
  const syncMut = useMutation({
    mutationFn: () => api.post<{ upserted: number; deleted: number }>('/api/calendar/sync', {}),
    onSuccess: (r) => { invalidate(); toast({ variant: 'success', message: `Synced program dates — ${r.upserted} on the calendar.` }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not sync program dates.' }),
  });

  // "What's coming due" look-ahead: today … today + N days, via the existing
  // date-range window (which forces the agenda). The PRD calls out 30 and 60.
  const applyLookahead = (days: number) => {
    const today = new Date();
    const to = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);
    setRangeFrom(iso(today));
    setRangeTo(iso(to));
    setDeadlinesOnly(false);
  };

  const rawEvents = eventsQuery.data ?? [];

  // Deep-link open: once the windowed events (jumped to ?date's month above)
  // include the ?event= id, pop its detail modal. Keyed on the id so clicking a
  // DIFFERENT dashboard event re-opens THAT event instead of a stale one.
  const lastFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusEventId || lastFocusRef.current === focusEventId) return;
    const match = rawEvents.find((e) => e.id === focusEventId);
    if (match) {
      lastFocusRef.current = focusEventId;
      setEditing(match);
    }
  }, [rawEvents, focusEventId]);
  // "Deadlines only" narrows the fetched window to the DEADLINE_TYPES client-side,
  // BEFORE every downstream derivation (byDay / upcoming / agenda), so it composes
  // with the date range to yield "all deadlines between X and Y".
  const events = useMemo(
    () => (deadlinesOnly ? rawEvents.filter((e) => (DEADLINE_TYPES as readonly string[]).includes(e.event_type)) : rawEvents),
    [rawEvents, deadlinesOnly],
  );
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const list = m.get(e.event_date) ?? [];
      list.push(e);
      m.set(e.event_date, list);
    }
    return m;
  }, [events]);

  const todayIso = iso(new Date());
  const upcoming = useMemo(
    () => events
      .filter((e) => e.event_date >= todayIso)
      .sort((a, b) => (a.event_date === b.event_date
        ? (a.event_time ?? '').localeCompare(b.event_time ?? '')
        : a.event_date.localeCompare(b.event_date)))
      .slice(0, 12),
    [events, todayIso],
  );
  // "Past due" counts only DEADLINE-shaped types whose date has passed unfinished.
  // It deliberately does NOT count a past coaching session: sessions are the bulk
  // of what is on this calendar (they are synced from every program_sessions row),
  // and most of them are historic, so counting them would make this tile read in
  // the hundreds forever and mean nothing.
  const overdueCount = events.filter(
    (e) => e.event_date < todayIso
      && (DEADLINE_TYPES as readonly string[]).includes(e.event_type)
      && e.status !== 'done',
  ).length;
  // B4 — dates in this window that are still placeholders.
  const tentativeCount = events.filter(isTentative).length;

  // Agenda view: every event in the window, grouped by date (ascending), each
  // group's events ordered by time. This is the full month as a scannable list —
  // the mobile default and the desktop "Agenda" toggle.
  const agendaGroups = useMemo(() => {
    const groups = [...byDay.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, list]) => ({
        date,
        events: [...list].sort((a, b) => (a.event_time ?? '').localeCompare(b.event_time ?? '')),
      }));
    return groups;
  }, [byDay]);

  // Assignee filter options: "All staff", then "Me" (the signed-in user) pinned
  // directly beneath it, then every OTHER staff user. Self is dropped from the
  // lower list so the native <select> carries no duplicate value — picking "Me"
  // sets assigneeFilter=meId, identical to picking one's own name.
  const assigneeOptions = useMemo(() => {
    const list = usersQuery.data ?? [];
    return [
      { value: '', label: 'All staff' },
      ...(meId ? [{ value: meId, label: `Me — ${meName || 'me'}` }] : []),
      ...list.filter((u) => u.id !== meId).map((u) => ({ value: u.id, label: userLabel(list, u.id) })),
    ];
  }, [usersQuery.data, meId, meName]);

  if (!canView) {
    return (
      <div style={{ padding: 'var(--sp-5)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)' }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>You do not have permission to view the calendar.</p>
      </div>
    );
  }

  // Build the 6×7 grid of Date cells for the visible month.
  const cells: Date[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) cells.push(new Date(d));

  return (
    <>
      <header style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 'var(--sp-5)' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)', letterSpacing: 'var(--ls-tight)', color: 'var(--color-ink)' }}>Calendar</h1>
          <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
            Sessions, cohort milestones, due dates, and reminders — scoped to your book.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          <CalendarSubscribe />
          {canAdd && (
            <Button variant="secondary" onClick={() => syncMut.mutate()} loading={syncMut.isPending} title="Pull session, cohort, and information-request dates onto the calendar">
              Sync program dates
            </Button>
          )}
          {canAdd && <Button variant="primary" onClick={() => setCreating(true)}>+ New event</Button>}
        </div>
      </header>

      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <StatCardRow>
          <StatCard
            label="Events this window"
            value={String(events.length)}
            caption={rangeActive
              ? `${rangeFrom ? formatEventDate(rangeFrom) : '…'} – ${rangeTo ? formatEventDate(rangeTo) : '…'}`
              : `${MONTH_NAMES[month.getMonth()]} ${month.getFullYear()}`}
            accent="hunter"
          />
          <StatCard label="Upcoming" value={String(upcoming.length)} caption="next on the books" accent="info" />
          <StatCard label="Past due" value={String(overdueCount)} caption={overdueCount > 0 ? 'due dates passed' : 'all on track'} accent={overdueCount > 0 ? 'amber' : 'green'} />
          {/* B4 — how many dates in this window are still placeholders. Makes the
              "Tentative only" filter discoverable and tells a coach how much
              of what they are planning around could still move. */}
          <StatCard
            label="Tentative"
            value={String(tentativeCount)}
            caption={tentativeCount > 0 ? 'dates not confirmed yet' : 'every date confirmed'}
            accent={tentativeCount > 0 ? 'amber' : 'green'}
          />
        </StatCardRow>
      </div>

      {/* Toolbar: month nav (or the active date-range banner) + view toggle */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        {rangeActive ? (
          // A month grid can't represent an arbitrary window, so range mode
          // replaces the month nav with a "Showing …" banner + a Clear-dates escape.
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
              Showing {rangeFrom ? formatEventDate(rangeFrom) : 'the earliest'} – {rangeTo ? formatEventDate(rangeTo) : 'latest'}
            </span>
            <Button variant="secondary" size="sm" onClick={() => { setRangeFrom(''); setRangeTo(''); }}>Clear dates</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <Button variant="secondary" size="sm" onClick={() => setMonth((m) => addMonths(m, -1))}>‹ Prev</Button>
            <span style={{ minWidth: 160, textAlign: 'center', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
              {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
            </span>
            <Button variant="secondary" size="sm" onClick={() => setMonth((m) => addMonths(m, 1))}>Next ›</Button>
            <Button variant="ghost" size="sm" onClick={() => setMonth(firstOfMonth(new Date()))}>Today</Button>
            {/* "Coming due" look-aheads — jump to a today…+N window (PRD §5.3). */}
            <Button variant="ghost" size="sm" onClick={() => applyLookahead(30)} title="What's coming due in the next 30 days">Next 30 days</Button>
            <Button variant="ghost" size="sm" onClick={() => applyLookahead(60)} title="What's coming due in the next 60 days">Next 60 days</Button>
          </div>
        )}
        {/* View toggle: month grid vs agenda list. On a phone the grid is too
            dense, and in range mode the agenda is forced — so the control is
            hidden in both cases. */}
        {!isCompact && !rangeActive && (
          <div style={{ marginLeft: 'auto', display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)', overflow: 'hidden' }} role="tablist" aria-label="Calendar view">
            <Button variant={view === 'grid' ? 'primary' : 'ghost'} size="sm" onClick={() => setView('grid')} aria-pressed={view === 'grid'}>Month</Button>
            <Button variant={view === 'agenda' ? 'primary' : 'ghost'} size="sm" onClick={() => setView('agenda')} aria-pressed={view === 'agenda'}>Agenda</Button>
          </div>
        )}
      </div>

      {/* Filters: type · assignee (assigned_user_id) · business (client_id) · a
          From/To date range · a Deadlines-only toggle. Type/assignee/business are
          real backend query params that AND with server-side book scope; the date
          range drives the fetched window; Deadlines-only narrows it client-side. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
        <div style={{ minWidth: 170, flex: '1 1 170px' }}>
          <Select
            aria-label="Type"
            options={[{ value: '', label: 'All types' }, ...TYPE_ORDER.map((t) => ({ value: t, label: TYPE_META[t].label }))]}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as '' | EventType)}
          />
        </div>
        <div style={{ minWidth: 190, flex: '1 1 190px' }}>
          <Select
            aria-label="Assigned to"
            options={assigneeOptions}
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          />
        </div>
        <div style={{ minWidth: 190, flex: '1 1 190px' }}>
          <Select
            aria-label="Business"
            options={[{ value: '', label: 'All businesses' }, ...(clientsQuery.data ?? []).map((c) => ({ value: c.id, label: c.name || c.id }))]}
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          />
        </div>
        {/* Region. Uses the shared canonical US-state list rather than deriving
            options from the loaded events: the visible window changes as you page
            months, and a filter whose options shift under you is worse than a long
            list. */}
        <div style={{ minWidth: 190, flex: '1 1 190px' }}>
          <Select
            aria-label="Region"
            options={[{ value: '', label: 'All regions' }, ...STATE_FILTER_OPTIONS]}
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
          />
        </div>
        {/* B4 — tentative vs confirmed. "Tentative only" is the worklist of dates
            still needing confirmation; it is a real backend param, so it composes
            with every filter above and with the date range. */}
        <div style={{ minWidth: 190, flex: '1 1 190px' }}>
          <Select
            aria-label="Date standing"
            options={[
              { value: '', label: 'All dates' },
              { value: 'tentative', label: 'Tentative only' },
              { value: 'confirmed', label: 'Confirmed only' },
            ]}
            value={confidenceFilter}
            onChange={(e) => setConfidenceFilter(e.target.value as '' | DateConfidence)}
          />
        </div>
        {/* F2 — keep From + To on ONE row as a single unit (they wrapped to
            separate lines on a phone when each was a bare flex child). flex-grow
            is 0 so the pair keeps its ~150px-each footprint on desktop (matching
            the original two fixed-width fields — no desktop reflow) while still
            shrinking to fit on a phone. */}
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flex: '0 1 300px', minWidth: 0 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', flex: '1 1 0', minWidth: 0 }}>
            <span style={filterLabelStyle}>From</span>
            <Input type="date" aria-label="From date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} invalid={rangeInvalid} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', flex: '1 1 0', minWidth: 0 }}>
            <span style={filterLabelStyle}>To</span>
            <Input type="date" aria-label="To date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} invalid={rangeInvalid} />
          </label>
        </div>
        {/* F2 — the deadline toggle + Clear filters get their own spaced group so
            they don't scrunch against the date inputs at mobile width. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
          <Checkbox label="Deadlines only" checked={deadlinesOnly} onChange={(e) => setDeadlinesOnly(e.target.checked)} />
          {(typeFilter || assigneeFilter || clientFilter || regionFilter || confidenceFilter || rangeActive || deadlinesOnly) && (
            <Button variant="ghost" size="sm" onClick={() => { setTypeFilter(''); setAssigneeFilter(''); setClientFilter(''); setRegionFilter(''); setConfidenceFilter(''); setRangeFrom(''); setRangeTo(''); setDeadlinesOnly(false); }}>Clear filters</Button>
          )}
        </div>
        {rangeInvalid && (
          <span style={{ flexBasis: '100%', fontSize: 'var(--fs-xs)', color: 'var(--color-status-red)' }}>
            The “From” date must be on or before the “To” date.
          </span>
        )}
      </div>

      {effectiveView === 'agenda' ? (
        <AgendaView
          groups={agendaGroups}
          loading={eventsQuery.isLoading}
          users={usersQuery.data ?? []}
          todayIso={todayIso}
          onOpen={setEditing}
        />
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(min(100%, 260px), 1fr)', gap: 'var(--sp-5)', alignItems: 'start' }}>
        {/* Month grid */}
        <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--color-surface-2, var(--color-card))' }}>
            {DOW.map((d) => (
              <div key={d} style={{ padding: 'var(--sp-2)', textAlign: 'center', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)', borderBottom: '1px solid var(--color-border)' }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {cells.map((cell) => {
              const key = iso(cell);
              const inMonth = cell.getMonth() === month.getMonth();
              const dayEvents = byDay.get(key) ?? [];
              const isToday = key === todayIso;
              return (
                <div key={key} style={{
                  // Fixed height (not minHeight) so every week row is the same
                  // size — a day with events no longer stretches its row taller
                  // than an empty one. The 2-event + "+N more" cap below keeps
                  // the content within this height.
                  height: 96, overflow: 'hidden',
                  padding: '2px 4px', borderRight: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)',
                  background: inMonth ? 'transparent' : 'var(--color-surface-2, rgba(0,0,0,0.02))', opacity: inMonth ? 1 : 0.6,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                    <span style={{
                      fontSize: 'var(--fs-xs)', fontWeight: isToday ? 'var(--fw-bold)' : 'var(--fw-medium)',
                      color: isToday ? 'var(--color-on-brand, #fff)' : 'var(--color-ink-2)',
                      background: isToday ? 'var(--color-brand, var(--color-hunter))' : 'transparent',
                      borderRadius: 'var(--r-full, 999px)', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>{cell.getDate()}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {dayEvents.slice(0, 2).map((e) => {
                      // The linked business, shown inline (muted) and repeated in the
                      // hover tooltip so a one-line cell event still reads as connected.
                      const tentative = isTentative(e);
                      const tip = [eventTitle(e), e.client_name, tentative ? 'TENTATIVE — not confirmed yet' : ''].filter(Boolean).join(' · ');
                      return (
                        <button
                          key={e.id}
                          onClick={() => setEditing(e)}
                          title={tip}
                          style={{
                            textAlign: 'left', cursor: 'pointer', borderRadius: 'var(--r-sm, 4px)',
                            padding: '1px 6px', fontSize: 'var(--fs-xs)', lineHeight: 1.35, color: 'var(--color-ink)', fontFamily: 'inherit',
                            background: 'var(--color-surface-2, rgba(0,0,0,0.04))',
                            // B4 — a tentative date reads as provisional at a glance: a
                            // DASHED outline (the convention for "not final") and a
                            // muted TENT marker, both of which survive the cell's
                            // one-line clamp better than a full badge would.
                            border: tentative ? '1px dashed var(--color-status-amber, var(--color-border))' : 'none',
                            borderLeft: `3px solid ${metaFor(e.event_type).accent}`,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}
                        >
                          {tentative ? <><TentativeBadge compact /><span>{' '}</span></> : null}
                          {e.event_time ? <span style={{ fontWeight: 'var(--fw-semibold)' }}>{`${formatEventTime(e.event_time)} `}</span> : ''}{eventTitle(e)}
                          {e.client_name ? <span style={{ color: 'var(--color-muted)' }}>{` · ${e.client_name}`}</span> : ''}
                        </button>
                      );
                    })}
                    {dayEvents.length > 2 && (
                      <button
                        onClick={() => setView('agenda')}
                        title={`Show all ${dayEvents.length} events for this day`}
                        style={{
                          textAlign: 'left', cursor: 'pointer', border: 'none', background: 'transparent',
                          padding: '0 6px', fontSize: 'var(--fs-xs)', lineHeight: 1.3, color: 'var(--color-muted)', fontFamily: 'inherit', fontWeight: 'var(--fw-medium)',
                        }}
                      >+{dayEvents.length - 2} more</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming list */}
        <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
          <h2 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Upcoming</h2>
          {eventsQuery.isLoading && <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>}
          {!eventsQuery.isLoading && upcoming.length === 0 && (
            <p style={{ color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No upcoming events in this window.</p>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {upcoming.map((e) => (
              <li key={e.id}>
                <button
                  onClick={() => setEditing(e)}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                    background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
                    padding: 'var(--sp-2) var(--sp-3)', borderLeft: `3px solid ${metaFor(e.event_type).accent}`,
                    ...tentativeRowStyle(e),
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-2)' }}>
                    <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)' }}>
                      {eventTitle(e)}
                    </span>
                    <span style={{ display: 'inline-flex', gap: 'var(--sp-1)', alignItems: 'center' }}>
                      {isTentative(e) && <TentativeBadge />}
                      <Badge variant={metaFor(e.event_type).variant}>{metaFor(e.event_type).label}</Badge>
                    </span>
                  </div>
                  <div style={{ marginTop: 2, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                    {formatEventDate(e.event_date)}{e.event_time ? ` · ${formatEventTime(e.event_time)}` : ''}{e.location ? ` · ${e.location}` : ''}
                    {e.assigned_user_id ? ` · ${userLabel(usersQuery.data ?? [], e.assigned_user_id)}` : ''}
                    {sourceLabel(e.source) ? ` · ${sourceLabel(e.source)}` : ''}
                  </div>
                  {/* Reminder fire-time, rendered in the ORG timezone (never raw UTC). */}
                  {e.remind_at && (
                    <div style={{ marginTop: 2, fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
                      Reminder: {formatAbsInTz(e.remind_at, orgTz)}
                    </div>
                  )}
                  {/* The linked business, so the event reads as connected. */}
                  <EventLinkage event={e} style={{ marginTop: 'var(--sp-1)' }} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      )}

      {(creating || editing) && (
        <EventModal
          key={editing?.id ?? 'new'}
          event={editing}
          defaultDate={iso(month.getMonth() === new Date().getMonth() ? new Date() : firstOfMonth(month))}
          users={usersQuery.data ?? []}
          clients={clientsQuery.data ?? []}
          canEdit={editing ? canEdit : canAdd}
          canDelete={canDelete}
          saving={createMut.isPending || updateMut.isPending}
          deleting={deleteMut.isPending}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSubmit={(body) => {
            if (editing) updateMut.mutate({ id: editing.id, body });
            else createMut.mutate(body);
          }}
          onDelete={editing ? () => deleteMut.mutate(editing.id) : undefined}
          onSetDateConfidence={editing ? (to) => confidenceMut.mutate({ id: editing.id, to }) : undefined}
          settingConfidence={confidenceMut.isPending}
        />
      )}
    </>
  );
}

/**
 * AgendaView — the whole visible window as a date-grouped list. The desktop
 * "Agenda" toggle and the forced mobile layout both render this. Each event row
 * opens the event modal on click, and its linkage chip (EventLinkage) clicks
 * through directly to the business the event belongs to.
 */
function AgendaView({ groups, loading, users, todayIso, onOpen }: {
  groups: Array<{ date: string; events: CalendarEvent[] }>;
  loading: boolean;
  users: UserLite[];
  todayIso: string;
  onOpen: (e: CalendarEvent) => void;
}) {
  function fmtDate(d: string): string {
    const [y, m, day] = d.split('-').map(Number);
    const dt = new Date(y, m - 1, day);
    return `${DOW[dt.getDay()]} · ${MONTH_NAMES[dt.getMonth()]} ${day}, ${y}`;
  }
  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      {loading && <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>}
      {!loading && groups.length === 0 && (
        <p style={{ color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No events in this window.</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
        {groups.map((g) => {
          const isToday = g.date === todayIso;
          const isPast = g.date < todayIso;
          return (
            <div key={g.date}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)',
                paddingBottom: 'var(--sp-1)', borderBottom: '1px solid var(--color-border)',
              }}>
                <h3 style={{
                  margin: 0, fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)',
                  color: isToday ? 'var(--color-brand, var(--color-hunter))' : isPast ? 'var(--color-ink-3)' : 'var(--color-ink)',
                }}>{fmtDate(g.date)}</h3>
                {isToday && <Badge variant="success">Today</Badge>}
                <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>{g.events.length} event{g.events.length === 1 ? '' : 's'}</span>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                {g.events.map((e) => (
                  <li key={e.id}>
                    <button
                      onClick={() => onOpen(e)}
                      style={{
                        width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                        background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
                        padding: 'var(--sp-2) var(--sp-3)', borderLeft: `3px solid ${metaFor(e.event_type).accent}`,
                        ...tentativeRowStyle(e),
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-2)' }}>
                        <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)' }}>
                          {e.event_time ? `${formatEventTime(e.event_time)} · ` : ''}{eventTitle(e)}
                        </span>
                        <span style={{ display: 'inline-flex', gap: 'var(--sp-1)', alignItems: 'center' }}>
                          {isTentative(e) && <TentativeBadge />}
                          <Badge variant={metaFor(e.event_type).variant}>{metaFor(e.event_type).label}</Badge>
                        </span>
                      </div>
                      <div style={{ marginTop: 2, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                        {e.location ? `${e.location}` : ''}
                        {e.assigned_user_id ? `${e.location ? ' · ' : ''}${userLabel(users, e.assigned_user_id)}` : ''}
                        {sourceLabel(e.source) ? `${e.location || e.assigned_user_id ? ' · ' : ''}${sourceLabel(e.source)}` : ''}
                      </div>
                      {/* The linked business — the chip clicks through to its record. */}
                      <EventLinkage event={e} style={{ marginTop: 'var(--sp-1)' }} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
